// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import React from 'react'
import styled from 'styled-components'

import { constantQuery, useQueryResult } from 'lib-common/query'
import Checkbox from 'lib-components/atoms/form/Checkbox'
import InputField from 'lib-components/atoms/form/InputField'
import { errorToInputInfo } from 'lib-components/input-info-helper'
import AdaptiveFlex from 'lib-components/layout/AdaptiveFlex'
import {
  FixedSpaceColumn,
  FixedSpaceFlexWrap,
  FixedSpaceRow
} from 'lib-components/layout/flex-helpers'
import ExpandingInfo from 'lib-components/molecules/ExpandingInfo'
import { PersonName } from 'lib-components/molecules/PersonNames'
import DatePicker from 'lib-components/molecules/date-picker/DatePicker'
import { H3, Label, P } from 'lib-components/typography'
import { Gap } from 'lib-components/white-space'

import type { ContactInfoSectionProps } from './ContactInfoSection'

export default React.memo(function GuardianSubSection({
  deps,
  application,
  formData,
  updateFormData,
  errors,
  verificationRequested
}: ContactInfoSectionProps) {
  const {
    translations: t,
    lang,
    emailVerificationStatusQuery,
    renderResult
  } = deps
  const { employeeTexts } = deps
  const employee = deps.actor === 'employee'
  const verifiedEmail = useQueryResult(
    emailVerificationStatusQuery !== null
      ? emailVerificationStatusQuery()
      : constantQuery(null)
  ).map((res) => (res === null ? null : res.verifiedEmail))

  if (
    verifiedEmail.isSuccess &&
    verifiedEmail.value &&
    formData.guardianEmail !== verifiedEmail.value
  ) {
    updateFormData({
      guardianEmail: verifiedEmail.value,
      guardianEmailVerification: verifiedEmail.value,
      noGuardianEmail: false
    })
  }

  return (
    <>
      <H3>{t.applications.editor.contactInfo.guardianInfoTitle}</H3>

      <AdaptiveFlex $breakpoint="1060px" $horizontalSpacing="XL">
        <FixedSpaceColumn $spacing="xs">
          <Label>{t.applications.editor.contactInfo.guardianFirstName}</Label>
          <span data-qa="guardian-first-name">
            <PersonName
              person={{ firstName: formData.guardianFirstName }}
              format="First"
            />
          </span>
        </FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xs">
          <Label>{t.applications.editor.contactInfo.guardianLastName}</Label>
          <span data-qa="guardian-last-name">
            <PersonName
              person={{ lastName: formData.guardianLastName }}
              format="Last"
            />
          </span>
        </FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xs">
          <Label>{t.applications.editor.contactInfo.guardianSSN}</Label>
          <span data-qa="guardian-ssn">{formData.guardianSSN}</span>
        </FixedSpaceColumn>
      </AdaptiveFlex>

      <Gap $size="s" />

      <FixedSpaceColumn $spacing="xs">
        <Label>{t.applications.editor.contactInfo.homeAddress}</Label>
        {employeeTexts && application.guardianRestricted ? (
          <span data-qa="guardian-restricted">
            {employeeTexts.addressRestricted}
          </span>
        ) : (
          <span data-qa="guardian-home-address" translate="no">
            {formData.guardianHomeAddress}
          </span>
        )}
      </FixedSpaceColumn>

      <Gap $size="s" />

      <FixedSpaceRow $spacing="XL">
        <AdaptiveFlex $breakpoint="860px">
          <FixedSpaceColumn $spacing="xs">
            <Label htmlFor="guardian-phone">
              {t.applications.editor.contactInfo.phone + (employee ? '' : ' *')}
            </Label>
            <InputField
              id="guardian-phone"
              type="tel"
              value={formData.guardianPhone}
              data-qa="guardianPhone-input"
              onChange={(value) => updateFormData({ guardianPhone: value })}
              info={errorToInputInfo(errors.guardianPhone, t.validationErrors)}
              hideErrorsBeforeTouched={!verificationRequested}
              placeholder={t.applications.editor.contactInfo.phone}
              width="m"
              required={!employee}
            />
          </FixedSpaceColumn>
        </AdaptiveFlex>
      </FixedSpaceRow>
      <Gap $size="m" />

      {employee ? (
        <FixedSpaceColumn $spacing="xs">
          <Label htmlFor="guardian-email">
            {t.applications.editor.contactInfo.email}
          </Label>
          <InputField
            id="guardian-email"
            type="email"
            value={formData.guardianEmail}
            data-qa="guardianEmail-input"
            onChange={(value) =>
              updateFormData({
                guardianEmail: value,
                guardianEmailVerification: value,
                noGuardianEmail: false
              })
            }
            info={errorToInputInfo(errors.guardianEmail, t.validationErrors)}
            hideErrorsBeforeTouched={!verificationRequested}
            width="L"
          />
        </FixedSpaceColumn>
      ) : (
        <EmailRow $breakpoint="860px" $verticalSpacing="zero">
          {renderResult(verifiedEmail, (verifiedEmail) =>
            verifiedEmail ? (
              <FixedSpaceColumn $spacing="xs">
                <Label>{t.applications.editor.contactInfo.email + ' *'}</Label>
                <FixedSpaceFlexWrap
                  $horizontalSpacing="X3L"
                  $verticalSpacing="L"
                >
                  <span translate="no" data-qa="verified-email">
                    {verifiedEmail}
                  </span>
                  <div>
                    {t.applications.editor.contactInfo.emailChangeTip}
                    <a
                      href="/personal-details"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.applications.editor.contactInfo.emailChangeTipLink}
                    </a>
                  </div>
                </FixedSpaceFlexWrap>
              </FixedSpaceColumn>
            ) : (
              <>
                <FixedSpaceColumn $spacing="xs">
                  <Label htmlFor="guardian-email">
                    {t.applications.editor.contactInfo.email}
                    {formData.noGuardianEmail ? '' : ' *'}
                  </Label>
                  <InputField
                    id="guardian-email"
                    type="email"
                    value={formData.guardianEmail}
                    data-qa="guardianEmail-input"
                    onChange={(value) =>
                      updateFormData({
                        guardianEmail: value,
                        noGuardianEmail: false
                      })
                    }
                    info={errorToInputInfo(
                      errors.guardianEmail,
                      t.validationErrors
                    )}
                    hideErrorsBeforeTouched={!verificationRequested}
                    placeholder={t.applications.editor.contactInfo.email}
                    width="L"
                    required={!formData.noGuardianEmail}
                    readonly={formData.noGuardianEmail}
                  />
                  <Gap $size="xs" />
                  <Label htmlFor="verify-guardian-email">
                    {t.applications.editor.contactInfo.verifyEmail}
                    {formData.noGuardianEmail ? '' : ' *'}
                  </Label>
                  <InputField
                    id="verify-guardian-email"
                    type="email"
                    value={formData.guardianEmailVerification}
                    data-qa="guardianEmailVerification-input"
                    onChange={(value) =>
                      updateFormData({
                        guardianEmailVerification: value,
                        noGuardianEmail: false
                      })
                    }
                    info={errorToInputInfo(
                      errors.guardianEmailVerification,
                      t.validationErrors
                    )}
                    hideErrorsBeforeTouched={!verificationRequested}
                    placeholder={t.applications.editor.contactInfo.verifyEmail}
                    width="L"
                    required={!formData.noGuardianEmail}
                    readonly={formData.noGuardianEmail}
                  />
                </FixedSpaceColumn>
                <div>
                  <Gap $size="m" />
                  <Checkbox
                    label={t.applications.editor.contactInfo.noEmail}
                    checked={formData.noGuardianEmail}
                    data-qa="noGuardianEmail-input"
                    onChange={(checked) =>
                      updateFormData({
                        guardianEmail: '',
                        guardianEmailVerification: '',
                        noGuardianEmail: checked
                      })
                    }
                  />
                </div>
              </>
            )
          )}
        </EmailRow>
      )}
      <Gap $size="m" />

      <P>{t.applications.editor.contactInfo.emailInfoText}</P>
      <Gap $size="m" />

      {!(employeeTexts && application.guardianRestricted) && (
        <>
          <ExpandingInfo
            data-qa="guardian-future-address-info"
            info={t.applications.editor.contactInfo.futureAddressInfo}
          >
            <Checkbox
              label={t.applications.editor.contactInfo.hasFutureAddress}
              checked={formData.guardianFutureAddressExists}
              data-qa="guardianFutureAddressExists-input"
              onChange={(checked) => {
                updateFormData({
                  guardianFutureAddressExists: checked
                })
              }}
            />
          </ExpandingInfo>
          {formData.guardianFutureAddressExists && (
            <>
              <Gap $size="m" />
              {formData.childFutureAddressExists && (
                <>
                  <Checkbox
                    label={
                      t.applications.editor.contactInfo
                        .guardianFutureAddressEqualsChildFutureAddress
                    }
                    checked={formData.guardianFutureAddressEqualsChild}
                    data-qa="guardianFutureAddressEqualsChild-input"
                    onChange={(checked) => {
                      updateFormData({
                        guardianFutureAddressEqualsChild: checked
                      })
                      if (checked) {
                        updateFormData({
                          guardianMoveDate: formData.childMoveDate,
                          guardianFutureStreet: formData.childFutureStreet,
                          guardianFuturePostalCode:
                            formData.childFuturePostalCode,
                          guardianFuturePostOffice:
                            formData.childFuturePostOffice
                        })
                      }
                    }}
                  />
                  <Gap $size="m" />
                </>
              )}
              <FixedSpaceColumn $spacing="xs">
                <Label htmlFor="guardian-move-date">
                  {t.applications.editor.contactInfo.moveDate + ' *'}
                </Label>
                <DatePicker
                  id="guardian-move-date"
                  required
                  date={formData.guardianMoveDate}
                  data-qa="guardianMoveDate-input"
                  onChange={(value) =>
                    updateFormData({ guardianMoveDate: value })
                  }
                  locale={lang}
                  info={errorToInputInfo(
                    errors.guardianMoveDate,
                    t.validationErrors
                  )}
                  hideErrorsBeforeTouched={!verificationRequested}
                />
              </FixedSpaceColumn>
              <Gap $size="s" />
              <FixedSpaceRow $spacing="XL">
                <AdaptiveFlex $breakpoint="1060px">
                  <FixedSpaceColumn $spacing="xs">
                    <Label htmlFor="guardian-future-street">
                      {t.applications.editor.contactInfo.street + ' *'}
                    </Label>
                    <InputField
                      id="guardian-future-street"
                      value={formData.guardianFutureStreet}
                      data-qa="guardianFutureStreet-input"
                      onChange={(value) =>
                        updateFormData({
                          guardianFutureStreet: value
                        })
                      }
                      info={errorToInputInfo(
                        errors.guardianFutureStreet,
                        t.validationErrors
                      )}
                      hideErrorsBeforeTouched={!verificationRequested}
                      placeholder={
                        t.applications.editor.contactInfo.streetPlaceholder
                      }
                      readonly={formData.guardianFutureAddressEqualsChild}
                      width="L"
                      required
                    />
                  </FixedSpaceColumn>
                  <FixedSpaceColumn $spacing="xs">
                    <Label htmlFor="guardian-future-postal-code">
                      {t.applications.editor.contactInfo.postalCode + ' *'}
                    </Label>
                    <InputField
                      id="guardian-future-postal-code"
                      value={formData.guardianFuturePostalCode}
                      data-qa="guardianFuturePostalCode-input"
                      onChange={(value) =>
                        updateFormData({
                          guardianFuturePostalCode: value
                        })
                      }
                      info={errorToInputInfo(
                        errors.guardianFuturePostalCode,
                        t.validationErrors
                      )}
                      hideErrorsBeforeTouched={!verificationRequested}
                      placeholder={
                        t.applications.editor.contactInfo.postalCodePlaceholder
                      }
                      readonly={formData.guardianFutureAddressEqualsChild}
                      width="m"
                      required
                    />
                  </FixedSpaceColumn>
                  <FixedSpaceColumn $spacing="xs">
                    <Label htmlFor="guardian-future-post-office">
                      {t.applications.editor.contactInfo.postOffice + ' *'}
                    </Label>
                    <InputField
                      id="guardian-future-post-office"
                      value={formData.guardianFuturePostOffice}
                      data-qa="guardianFuturePostOffice-input"
                      onChange={(value) =>
                        updateFormData({
                          guardianFuturePostOffice: value
                        })
                      }
                      info={errorToInputInfo(
                        errors.guardianFuturePostOffice,
                        t.validationErrors
                      )}
                      hideErrorsBeforeTouched={!verificationRequested}
                      placeholder={
                        t.applications.editor.contactInfo
                          .municipalityPlaceholder
                      }
                      readonly={formData.guardianFutureAddressEqualsChild}
                      width="m"
                      required
                    />
                  </FixedSpaceColumn>
                </AdaptiveFlex>
              </FixedSpaceRow>
            </>
          )}
        </>
      )}
    </>
  )
})

const EmailRow = styled(AdaptiveFlex)`
  align-items: flex-start;
`
